import React from 'react';

import { trackEvent } from '../analytics/tracking';
import { UserContext } from '../providers/UserProvider';
import { DispensariesContext} from '../dispensaries/DispensariesProvider';
import { getDistanceToDispensaries } from '../googlemaps/google-maps-utils';

/**
 * This hook uses the UserContext, the DispensariesContext and the Google Maps API
 * to sort dispensaries based on proximity to the user's address.
 * 
 * Note: A state of loading:false and dispensariesByDistance.length === 0 would
 * imply that we don't have an address for the user.
 */

const useDispensaryProximity = () => {
  
  const { dispensaries } = React.useContext(DispensariesContext);
  const { location, searchLocation } = React.useContext(UserContext);

  // Loading state
  const [loading, setLoading] = React.useState(true);
  const [userZipCode, setUserZipCode] = React.useState();
  const [dispensariesByDistance, setDispensariesByDistance] = React.useState([]);
  const [distanceMap, setDistanceMap] = React.useState(new Map());
  
  /**
   * This is for the purpose of sorting dispensaries based on proximity, 
   * and getting the distance from the user's home to the dispensary
   */
  React.useEffect(() => {
    const callback = (response) => {
      try {
        // distances in meters from Google
        const distances = response.rows[0].elements;
        // Create arrays in the order requested (dispensary order)
        const distanceArray = distances.map(distanceInfo => distanceInfo.distance.value);
        const distanceTextArray = distances.map(distanceInfo => distanceInfo.distance.text);
        
        // Add travel distance to dispansary object copies
        const dispensariesWithDistance = dispensaries.reduce((dispArray, disp, idx) => {
          const dispensaryCopy = Object.assign({}, disp);
          dispensaryCopy.travelDistance = distanceArray[idx];
          dispensaryCopy.travelDistanceText = distanceTextArray[idx];
          dispArray.push(dispensaryCopy);
          return dispArray;   
        }, []);
        
        // Create the sorted closestDispensaries array
        const sorteDispensaries = dispensariesWithDistance.slice().sort((a, b) => {  
          return a.travelDistance - b.travelDistance;
        });
        
        setDispensariesByDistance(sorteDispensaries);         
        setDistanceMap(
          dispensariesWithDistance.reduce((map, dispensary) => map.set(dispensary.id, dispensary.travelDistanceText), new Map())
        );
      } catch(e) {
        // Google Maps Error?
        trackEvent('error_calculating_dispensary_distance');
        // We need to supply some dispensaries, sorted or not.
        setDispensariesByDistance(dispensaries);
      }
      setLoading(false);
    };

    // Costs $, try not to do this more than once
    if (dispensaries?.length && (location || searchLocation)) { 
      const { street_address, city, state, zip_code } = location || searchLocation;
      const fromAddress = `${street_address}, ${city}, ${state}, ${zip_code}`;
      if (zip_code !== userZipCode) {
        setLoading(true);
        console.log('useDispensariesByDistance - fetching distance');
        getDistanceToDispensaries(fromAddress, dispensaries, callback);
        setUserZipCode(zip_code);
      }
    } else {
       setLoading(false);
    }
  }, [location, searchLocation, userZipCode, dispensaries]);  
  
  return { dispensariesByDistance, distanceMap, userZipCode, loading };
}

export default useDispensaryProximity;